import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  closeRefundMutation,
  resendRefundEmailMutation,
  RefundRecord,
} from "@/app/Refunds/GraphQL/refundMutations";
import { RefundStatus } from "@/gql/graphql";
import { ExternalLinkIcon, MailIcon, XCircleIcon } from "lucide-react";
import { toast } from "sonner";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}

export function useRefundActions(): ActionsHook<RefundRecord> {
  const { mutateAsync: resendEmail } = useGqlMutation(
    resendRefundEmailMutation,
  );
  const { mutateAsync: closeRefund } = useGqlMutation(closeRefundMutation);
  const { open: openConfirmDelete } = useConfirmDeleteDialog();

  return (refund) => {
    const isClosed =
      refund.status === RefundStatus.Completed ||
      refund.status === RefundStatus.Rejected;

    return [
      {
        label: "Actions",
        items: [
          {
            actions: [
              {
                label: "Resend email",
                Icon: MailIcon,
                isHidden: isClosed,
                handleAction: async () => {
                  try {
                    await resendEmail({ id: refund.id });
                    toast.success("Refund email resent");
                  } catch (err: unknown) {
                    toast.error(errorMessage(err, "Failed to resend email"));
                  }
                },
              },
              {
                label: "View refund form",
                Icon: ExternalLinkIcon,
                isHidden: !refund.permalink,
                handleAction: () => {
                  if (refund.permalink) {
                    window.open(
                      refund.permalink,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }
                },
              },
              {
                label: "Close refund",
                Icon: XCircleIcon,
                color: "red",
                isHidden: isClosed,
                handleAction: () => {
                  openConfirmDelete({
                    title: "Close refund",
                    message:
                      "Close this refund? The customer will no longer be able to submit receipts.",
                    buttonText: "Close refund",
                    onDelete: async () => {
                      try {
                        await closeRefund({ id: refund.id });
                        toast.success("Refund closed");
                      } catch (err: unknown) {
                        toast.error(
                          errorMessage(err, "Failed to close refund"),
                        );
                      }
                    },
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
